import React, { useState, useEffect, useRef } from "react";
import "./ProfileAll.css";
import DobInput from "../DobInput";
import { useAuthContext } from "../../hooks/useAuthContext";
import { getAuth } from "firebase/auth";
import firebase from "../../Firebase";
import Cookies from "js-cookie";

const ProfileBasics = ({ setProfileInfo, hideDob = false }) => {
  const [formData, setFormData] = useState({
    user: { firstName: "", dateOfBirth: null, gender: "" },
  });
  const [loading, setLoading] = useState(true);
  const [profileComplete, setIsProfileComplete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Accounts with no email — Facebook sign-ups whose Facebook account was
  // made with a phone number (Facebook never shares the number). We need an
  // email to tell them about suggestions and messages, so it's asked for
  // here (onboarding step 1, and Profile → Basic Info until given).
  const [needsEmail, setNeedsEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSaved, setEmailSaved] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const { user } = useAuthContext();
  const abortControllerRef = useRef(new AbortController());

  // Get fresh tokens directly from Firebase instead of using state
  const getTokens = async () => {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken(true);
      const csrfToken = Cookies.get("mytoken");
      return { idToken, csrfToken };
    } catch (error) {
      console.error("Token error:", error);
      window.location.href = '/login';
      throw error;
    }
  };

  // Fetch profile data with automatic token refresh
  const fetchProfile = async () => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      const { idToken, csrfToken } = await getTokens();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/userProfile/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({ _id: user?.userId }),
          signal: abortControllerRef.current.signal
        }
      );

      if (response.status === 403) {
        await firebase.auth().currentUser.getIdToken(true);
        return fetchProfile();
      }

      if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

      const data = await response.json();
      setNeedsEmail(prev => prev || !data.user?.email);
      setFormData({
        user: {
          firstName: data.user?.firstName || "",
          dateOfBirth: data.user?.dateOfBirth
            ? new Date(data.user.dateOfBirth)
            : null,
          gender: data.user?.gender || ""
        }
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Fetch error:", error);
      }
    } finally {
      setLoading(false);
    }
  };
 
  // Initial fetch
  useEffect(() => {
    if (user?.userId) fetchProfile();
    return () => abortControllerRef.current.abort();
  }, [user?.userId]);

  // Update profile completeness
  useEffect(() => {
    const isComplete = formData.user.firstName.trim() && formData.user.gender && (hideDob || formData.user.dateOfBirth)
      && (!needsEmail || !!emailSaved);
    setIsProfileComplete(isComplete);
    setProfileInfo?.(isComplete);
  }, [formData, setProfileInfo, needsEmail, emailSaved]);

  // Save the email (only fills an empty one — POST /userProfile/add-email),
  // then have Firebase email a confirm link; confirming marks it verified,
  // which introductions need.
  const saveEmail = async () => {
    const email = emailDraft.trim();
    if (!email || email.toLowerCase() === emailSaved || emailSaving) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("That doesn't look like an email address.");
      return;
    }
    setEmailSaving(true);
    setEmailError("");
    setEmailNote("");
    try {
      const { idToken, csrfToken } = await getTokens();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/add-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}`, "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setEmailError(data.error || "Couldn't save your email. Please try again.");
        return;
      }
      setEmailSaved(data.email);
      try {
        await firebase.auth().currentUser.verifyBeforeUpdateEmail(data.email, {
          url: `${window.location.origin}/ProfileUpdate`,
        });
        setEmailNote(`Saved. We've emailed ${data.email} a link — please tap it to confirm it's yours. We can't introduce you to anyone until it's confirmed.`);
      } catch (err) {
        console.error("[ProfileBasics] confirm link:", err.code || err.message);
        setEmailNote("Saved.");
      }
    } catch (err) {
      setEmailError("Couldn't save your email. Please try again.");
    } finally {
      setEmailSaving(false);
    }
  };

  // Handle profile updates
  const updateProfile = async (updatedData) => {
    const controller = new AbortController();
    
    try {
      const { idToken, csrfToken } = await getTokens();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({
            userId: user.userId,
            UserBasics: updatedData
          }),
          signal: controller.signal
        }
      );

      if (response.status === 403) {
        await firebase.auth().currentUser.getIdToken(true);
        return updateProfile(updatedData);
      }

      if (!response.ok) throw new Error(`Update failed: ${response.status}`);
    } catch (error) {
      if (error.name !== 'AbortError') throw error;
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    const newData = {
      ...formData.user,
      [name]: name === "dateOfBirth" ? new Date(value) : value
    };
    
    console.log(`[ProfileBasics] Field changed: ${name} = ${value}`);
    console.log(`[ProfileBasics] New data object:`, newData);
    
    setFormData({ user: newData });
    
    if (name === "dateOfBirth") {
      const age = new Date().getFullYear() - newData.dateOfBirth.getFullYear();
      if (age < 18) {
        setShowModal(true);
        return;
      }
    }
    
    console.log(`[ProfileBasics] Calling updateProfile with:`, newData);
    await updateProfile(newData);
  };

  const handleModalClose = () => setShowModal(false);

  return (
    <div className="profile-basics-container">
      {loading ? (
        <div className="loading-indicator">Loading profile...</div>
      ) : (
        <form className="profile-form">
          <div className="form-header">
            {/* <h2>Basic Information</h2> */}
            {/* <div className={`status-indicator ${profileComplete ? 'complete' : 'incomplete'}`}>
              {profileComplete ? '✓ Complete' : '⚠️ Incomplete'}
            </div> */}
          </div>

          {needsEmail && (
            <div className="form-group">
              <label htmlFor="email">Email address:</label>
              <p className="form-note" style={{ marginTop: 0 }}>
                Facebook didn&apos;t share an email address with us. We need one to let you know about people you&apos;re suggested and messages.
              </p>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={emailDraft}
                onChange={(e) => { setEmailDraft(e.target.value); setEmailError(""); }}
                onBlur={saveEmail}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEmail(); } }}
                placeholder="you@example.com"
                disabled={emailSaving}
              />
              {emailSaving && <div className="form-note">Saving…</div>}
              {emailError && <div className="form-note" style={{ color: "#d32f2f" }}>{emailError}</div>}
              {emailNote && !emailError && <div className="form-note" style={{ color: "#15803d" }}>{emailNote}</div>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="firstName">First Name:</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.user.firstName}
              onChange={handleInputChange}
              placeholder="Enter your first name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender:</label>
            <select
              id="gender"
              name="gender"
              value={formData.user.gender}
              onChange={handleInputChange}
            >
              <option value="">Select your gender</option>
              <option value="Woman">Woman</option>
              <option value="Man">Man</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Transgender Woman">Transgender Woman</option>
              <option value="Transgender Man">Transgender Man</option>
              <option value="Gender Fluid">Gender Fluid</option>
              <option value="Genderqueer">Genderqueer</option>
              <option value="Agender">Agender</option>
              <option value="Two-Spirit">Two-Spirit</option>
              <option value="Demigender">Demigender</option>
              <option value="Questioning">Questioning</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>

          {!hideDob && <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth:</label>
            <DobInput
              id="dateOfBirth"
              value={formData.user.dateOfBirth instanceof Date && !isNaN(formData.user.dateOfBirth) ? formData.user.dateOfBirth.toISOString().split('T')[0] : ''}
              onChange={(iso) => handleInputChange({ target: { name: 'dateOfBirth', value: iso } })}
            />
          </div>}

          {!profileComplete && (
            <div className="form-note">
              * All fields are required to complete your profile
            </div>
          )}
        </form>
      )}

      {/* Modal for under-18 DOB */}
      {showModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '1.1em' }}>
                You must be 18 years or older to use this platform.
              </span>
              <button 
                id="age-verification-close-btn"
                onClick={handleModalClose} 
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileBasics;


// import React, { useState, useEffect, useRef } from "react";
// import "./ProfileAll.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { getAuth } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// const ProfileBasics = ({ setProfileInfo }) => {
//   const [formData, setFormData] = useState({
//     user: { firstName: "", dateOfBirth: null },
//   });
//   const [loading, setLoading] = useState(true);
//   const [profileComplete, setIsProfileComplete] = useState(false);
//   const { user } = useAuthContext();
//   const abortControllerRef = useRef(new AbortController());

//   // Get fresh tokens directly from Firebase instead of using state
//   const getTokens = async () => {
//     try {
//       const idToken = await firebase.auth().currentUser.getIdToken(true);
//       const csrfToken = Cookies.get("mytoken");
//       return { idToken, csrfToken };
//     } catch (error) {
//       console.error("Token error:", error);
//       window.location.href = '/login';
//       throw error;
//     }
//   };

//   // Fetch profile data with automatic token refresh
//   const fetchProfile = async () => {
//     abortControllerRef.current.abort();
//     abortControllerRef.current = new AbortController();
    
//     try {
//       setLoading(true);
//       const { idToken, csrfToken } = await getTokens();

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userProfile/`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken
//           },
//           body: JSON.stringify({ _id: user?.userId }),
//           signal: abortControllerRef.current.signal
//         }
//       );

//       if (response.status === 403) {
//         // Force token refresh and retry
//         await firebase.auth().currentUser.getIdToken(true);
//         return fetchProfile();
//       }

//       if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

//       const data = await response.json();
//       setFormData({
//         user: {
//           firstName: data.user?.firstName || "",
//           dateOfBirth: data.user?.dateOfBirth 
//             ? new Date(data.user.dateOfBirth)
//             : null
//         }
//       });
//     } catch (error) {
//       if (error.name !== 'AbortError') {
//         console.error("Fetch error:", error);
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Initial fetch
//   useEffect(() => {
//     if (user?.userId) fetchProfile();
//     return () => abortControllerRef.current.abort();
//   }, [user?.userId]);

//   // Update profile completeness
//   useEffect(() => {
//     const isComplete = formData.user.firstName.trim() && formData.user.dateOfBirth;
//     setIsProfileComplete(isComplete);
//     setProfileInfo?.(isComplete);
//   }, [formData, setProfileInfo]);

//   // Handle profile updates
//   const updateProfile = async (updatedData) => {
//     const controller = new AbortController();
    
//     try {
//       const { idToken, csrfToken } = await getTokens();

//       const response = await fetch(
//         `${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             "Authorization": `Bearer ${idToken}`,
//             "X-CSRF-Token": csrfToken
//           },
//           body: JSON.stringify({
//             userId: user.userId,
//             UserBasics: updatedData
//           }),
//           signal: controller.signal
//         }
//       );

//       if (response.status === 403) {
//         await firebase.auth().currentUser.getIdToken(true);
//         return updateProfile(updatedData);
//       }

//       if (!response.ok) throw new Error(`Update failed: ${response.status}`);
//     } catch (error) {
//       if (error.name !== 'AbortError') throw error;
//     }
//   };

//   const handleInputChange = async (e) => {
//     const { name, value } = e.target;
//     const newData = {
//       ...formData.user,
//       [name]: name === "dateOfBirth" ? new Date(value) : value
//     };
    
//     setFormData({ user: newData });
    
//     if (name === "dateOfBirth") {
//       const age = new Date().getFullYear() - newData.dateOfBirth.getFullYear();
//       if (age < 18) {
//         alert("You must be 18 years or older");
//         return;
//       }
//     }
    
//     await updateProfile(newData);
//   };

//   return (
//     <div className="profile-basics-container">
//       {loading ? (
//         <div className="loading-indicator">Loading profile...</div>
//       ) : (
//         <form className="profile-form">
//           <div className="form-header">
//             <h2>Basic Information</h2>
//             <div className={`status-indicator ${profileComplete ? 'complete' : 'incomplete'}`}>
//               {profileComplete ? '✓ Complete' : '⚠️ Incomplete'}
//             </div>
//           </div>

//           <div className="form-group">
//             <label htmlFor="firstName">First Name:</label>
//             <input
//               id="firstName"
//               name="firstName"
//               type="text"
//               value={formData.user.firstName}
//               onChange={handleInputChange}
//               placeholder="Enter your first name"
//             />
//           </div>

//           <div className="form-group">
//             <label htmlFor="dateOfBirth">Date of Birth:</label>
//             <input
//               id="dateOfBirth"
//               name="dateOfBirth"
//               type="date"
//               value={formData.user.dateOfBirth?.toISOString().split('T')[0] || ''}
//               onChange={handleInputChange}
//             />
//           </div>

//           {!profileComplete && (
//             <div className="form-note">
//               * Both fields are required to complete your profile
//             </div>
//           )}
//         </form>
//       )}
//     </div>
//   );
// };

// export default ProfileBasics;


// import React, { useState, useEffect, useRef } from "react";
// import "./ProfileAll.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { initializeApp, getApps } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const ProfileBasics = ({ setProfileInfo }) => {
//   const [json, setJson] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [formData, setFormData] = useState({
//     user: { firstName: "", dateOfBirth: null },
//   });
//   const [shouldSendUpdate, setShouldSendUpdate] = useState(false);
//   const [token, setToken] = useState(null);
//   const [csrfToken, setCsrfToken] = useState(null);
//   const [profileComplete, setIsProfileComplete] = useState(false);
//   const { user } = useAuthContext();
//   const auth = getAuth();
//   const abortControllerRef = useRef(null);

//   // Token management
//   useEffect(() => {
//     const fetchToken = async () => {
//       const token = await firebase.auth().currentUser.getIdToken(true);
//       setToken(token);
//     };
//     fetchToken();
//   }, []);

//   // CSRF token handling
//   useEffect(() => {
//     const fetchCsrfToken = async () => {
//       const csrfToken = Cookies.get("mytoken");
//       setCsrfToken(csrfToken);
//     };
//     fetchCsrfToken();
//   }, []);

//   // Profile completion check
//   useEffect(() => {
//     if (formData.user.firstName || formData.user.dateOfBirth) {
//       const isComplete = formData.user.firstName.trim() !== "" && 
//                         formData.user.dateOfBirth !== null;
//       setIsProfileComplete(isComplete);
//       if (setProfileInfo) setProfileInfo(isComplete);
//     }
//   }, [formData, setProfileInfo]);

//   // Profile data fetching
//   useEffect(() => {
//     const controller = new AbortController();
//     abortControllerRef.current = controller;

//     const fetchProfile = async () => {
//       if (!user?.userId) return;

//       try {
//         setLoading(true);
        
//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/userProfile/`,
//           {
//             method: "POST",
//             credentials: 'include',
//             body: JSON.stringify({ _id: user.userId }),
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//               "X-CSRF-Token": csrfToken,
//             },
//             signal: controller.signal,
//           }
//         );

//         if (response.status === 403) {
//           const newToken = await firebase.auth().currentUser.getIdToken(true);
//           setToken(newToken);
//           return fetchProfile();
//         }

//         if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

//         const jsonData = await response.json();
//         setJson(jsonData);
//         setFormData({
//           user: {
//             firstName: jsonData.user?.firstName || "",
//             dateOfBirth: jsonData.user?.dateOfBirth 
//               ? new Date(jsonData.user.dateOfBirth) 
//               : null
//           }
//         });
//       } catch (error) {
//         if (error.name !== 'AbortError') {
//           console.error("Fetch error:", error);
//         }
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProfile();
//     return () => controller.abort();
//   }, [user, token, csrfToken]);

//   // Profile update handling
//   useEffect(() => {
//     const controller = new AbortController();
//     abortControllerRef.current = controller;

//     const sendPostRequest = async () => {
//       if (user?.userId && shouldSendUpdate) {
//         try {
//           const dateOfBirthValue = formData.user.dateOfBirth?.toISOString() || null;
          
//           const response = await fetch(
//             `${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`,
//             {
//               method: "POST",
//               body: JSON.stringify({
//                 userId: user.userId,
//                 UserBasics: {
//                   ...formData.user,
//                   dateOfBirth: dateOfBirthValue
//                 }
//               }),
//               headers: {
//                 "Content-Type": "application/json",
//                 Authorization: `Bearer ${token}`,
//                 "X-CSRF-Token": csrfToken
//               },
//               signal: controller.signal
//             }
//           );

//           if (response.status === 403) {
//             const newToken = await firebase.auth().currentUser.getIdToken(true);
//             setToken(newToken);
//             return sendPostRequest();
//           }

//           if (!response.ok) throw new Error(`Update failed: ${response.status}`);

//           const jsonData = await response.json();
//           console.log("Update successful:", jsonData);
//           setShouldSendUpdate(false);
//         } catch (error) {
//           if (error.name !== 'AbortError') {
//             console.error("Update error:", error);
//           }
//         }
//       }
//     };

//     sendPostRequest();
//     return () => controller.abort();
//   }, [shouldSendUpdate, formData, user, token, csrfToken]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({
//       user: {
//         ...prev.user,
//         [name]: name === "dateOfBirth" ? new Date(value) : value
//       }
//     }));
//     setShouldSendUpdate(name !== "dateOfBirth");
//   };

//   const handleDateBlur = () => {
//     if (formData.user.dateOfBirth) {
//       const age = new Date().getFullYear() - formData.user.dateOfBirth.getFullYear();
//       if (age < 18) {
//         alert("Must be 18+");
//         return;
//       }
//       setShouldSendUpdate(true);
//     }
//   };

//   return (
//     <div className="profile-basics-container">
//       {loading ? (
//         <div className="loading-indicator">Loading profile...</div>
//       ) : (
//         <form className="profile-form">
//           <div className="form-header">
//             <h2>Basic Information</h2>
//             <div className={`status-indicator ${profileComplete ? 'complete' : 'incomplete'}`}>
//               {profileComplete ? '✓ Complete' : '⚠️ Incomplete'}
//             </div>
//           </div>

//           <div className="form-group">
//             <label htmlFor="firstName">First Name:</label>
//             <input
//               id="firstName"
//               name="firstName"
//               type="text"
//               value={formData.user.firstName}
//               onChange={handleInputChange}
//               placeholder="Enter your first name"
//             />
//           </div>

//           <div className="form-group">
//             <label htmlFor="dateOfBirth">Date of Birth:</label>
//             <input
//               id="dateOfBirth"
//               name="dateOfBirth"
//               type="date"
//               value={formData.user.dateOfBirth?.toISOString().split('T')[0] || ''}
//               onChange={handleInputChange}
//               onBlur={handleDateBlur}
//             />
//           </div>

//           {!profileComplete && (
//             <div className="form-note">
//               * Both fields are required to complete your profile
//             </div>
//           )}
//         </form>
//       )}
//     </div>
//   );
// };

// export default ProfileBasics;

//works below? THink so 


// import React, { useState, useEffect } from "react";
// import "./ProfileAll.css";
// import { useAuthContext } from "../../hooks/useAuthContext";
// import { initializeApp, getApps } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import firebase from "../../Firebase";
// import Cookies from "js-cookie";

// if (!getApps().length) {
//   initializeApp(firebase);
// }

// const ProfileBasics = ({setProfileInfo}) => {
//   console.log("Rendering ProfileBasics");
//   const [json, setJson] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [formData, setFormData] = useState({
//     user: { firstName: "", dateOfBirth: null },
//   });
//   const [shouldSendUpdate, setShouldSendUpdate] = useState(false);
//   const [token, setToken] = useState(null);
//   const [csrfToken, setCsrfToken] = useState(null);
//   const [profileComplete, setIsProfileComplete] = useState(false);
//   const { user } = useAuthContext();
//   const auth = getAuth();
//   console.log("AUTH99,", auth.currentUser);

//   let currentController = null;

//   useEffect(() => {
//     const fetchToken = async () => {
//       const token = await firebase.auth().currentUser.getIdToken(true);
//       setToken(token);
//     };

//     fetchToken();
//   }, []);


//   useEffect(() => {
//     // Check if formData is defined and all required fields are filled out
//     if (formData.user.firstName) {
//       const checkProfileCompletion = () => {
//         console.log('formData.user.firstName', formData.user.firstName)
//         const isComplete = formData.user.firstName.trim() !== "" && formData.user.dateOfBirth !== null;
//         if (typeof setProfileInfo === 'function') {
//           setProfileInfo(isComplete);
//         }
//         setIsProfileComplete(isComplete);
//       };
  
//       checkProfileCompletion();
//     }
//   }, [formData]);
//   useEffect(() => {
//     const fetchCsrfToken = async () => {
//       const csrfToken = Cookies.get("mytoken");
//       setCsrfToken(csrfToken);
//     };

//     fetchCsrfToken();
//   }, []);

//   useEffect(() => {
//     if (user) {
//       const fetchProfile = async () => {
//         setLoading(true);
//         if (currentController) {
//           currentController.abort();
//         }

//         currentController = new AbortController();
//         const signal = currentController.signal;

//         const body = JSON.stringify({ _id: user.userId });
//         const headers = {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${token}`,
//           "X-CSRF-Token": csrfToken,
//         };

//         console.log("Sending request with body:", body);
//         console.log("Headers:", headers);

//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/userProfile/`,
//           {
//             method: "POST",
//             body: body,
//             headers: headers,
//             signal: signal,
//           }
//         );

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }

//         const json = await response.json();
//         setJson(json);
//         setLoading(false);
//         setFormData({
//           user: {
//             ...json.user,
//             dateOfBirth: json.user.dateOfBirth
//               ? new Date(json.user.dateOfBirth)
//               : null,
//           },
//         });
//         console.log("json:", json);
//       };

//       fetchProfile();
//     }
//   }, [user, token, csrfToken]);

//   useEffect(() => {
//     const sendPostRequest = async () => {
//       if (user && shouldSendUpdate) {
//         console.log("Updating UserBasics Profile info:", user.userId);

//         const dateOfBirthValue =
//           formData.user.dateOfBirth instanceof Date
//             ? formData.user.dateOfBirth.toISOString()
//             : formData.user.dateOfBirth;

//         const requestBody = {
//           userId: user.userId,
//           UserBasics: {
//             ...formData.user,
//             dateOfBirth: dateOfBirthValue,
//           },
//         };

//         console.log("Sending POST request with body:", requestBody);

//         if (currentController) {
//           currentController.abort();
//         }

//         currentController = new AbortController();
//         const signal = currentController.signal;

//         const response = await fetch(
//           `${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`,
//           {
//             method: "POST",
//             body: JSON.stringify(requestBody),
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//               "X-CSRF-Token": csrfToken,
//             },
//             signal: signal,
//           }
//         );

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }

//         const json = await response.json();
//         console.log("Response:", json);
//         setShouldSendUpdate(false);
//       }
//     };

//     sendPostRequest();
//   }, [shouldSendUpdate, formData, user, token, csrfToken]);

//   const handleInputChange = (event) => {
//     const { name, value } = event.target;
//     setFormData((prevFormData) => ({
//       ...prevFormData,
//       user: {
//         ...prevFormData.user,
//         [name]: name === "dateOfBirth" ? new Date(value) : value,
//       },
//     }));
//     if (name === "dateOfBirth") {
//       setShouldSendUpdate(false); // Prevent immediate update until onBlur validation
//     } else {
//       setShouldSendUpdate(true);
//     }
//   };

//   const handleDateOfBirthBlur = () => {
//     const birthDate = formData.user.dateOfBirth;
//     if (birthDate instanceof Date) {
//       const today = new Date();
//       let age = today.getFullYear() - birthDate.getFullYear();
//       const m = today.getMonth() - birthDate.getMonth();
//       if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
//         age--;
//       }
//       if (age < 18) {
//         alert("You must be 18 years or over.");
//         return;
//       }
//       setShouldSendUpdate(true); // Now allow update after validation
//     }
//   };

//   const form = (
//     <form className="dynamic-form">
//               <p>Profile Status: {profileComplete ? "Complete" : "Incomplete"}</p>

//       <div>
//         <label htmlFor="firstName">First Name:</label>
//         <input
//           type="text"
//           id="firstName"
//           name="firstName"
//           value={formData.user.firstName}
//           onChange={handleInputChange}
//         />
//         <label htmlFor="dateOfBirth">Date of Birth:</label>
//         <input
//           type="date"
//           id="dateOfBirth"
//           name="dateOfBirth"
//           value={
//             formData.user.dateOfBirth
//               ? formData.user.dateOfBirth.toISOString().slice(0, 10)
//               : ""
//           }
//           onChange={handleInputChange}
//           onBlur={handleDateOfBirthBlur}
//         />
//       </div>
//     </form>
//   );

//   return (
//     <div>
//       {loading ? <p>Loading...</p> : json && json.user && form}
//     </div>
//   );
// };

// export default ProfileBasics;